/**
 * Results Page — displays backend-computed assessment results.
 *
 * All scoring is done on the backend. The frontend only maps response shapes
 * to what each chart component expects (no score computation).
 */

import { useState, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ScoreSummary } from '@/features/results/components/score-summary'
import { RadarChartComponent } from '@/features/results/components/radar-chart'
import { GapAnalysisChart } from '@/features/results/components/gap-analysis-chart'
import { Recommendations } from '@/features/results/components/recommendations'
import { AnswersTab } from '@/features/results/components/answers-tab'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Target, BarChart3, TrendingUp, Download, FileText, ExternalLink, RefreshCw, List } from 'lucide-react'
import { AnimatedTabContent } from '@/components/ui/animated-tab-content'
import { useResults, useRecalculateScores } from '@/features/results/hooks'
import { useAssessmentProgress } from '@/features/assessments/hooks'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import { useAuthentication } from '@/features/auth/AuthenticationContext'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import { getMaturityColorForLevel } from '@/features/maturity-levels/maturity-color-utils'
import type { MaturityLevel } from '@/features/maturity-levels/types'
import { motion } from 'framer-motion'

// ── Helpers — map backend shapes to chart component props ─────────────────

/** Map backend domain_scores dict → ScoreSummary / chart arrays */
function mapDomainScores(domainScoresDict: Record<string, any>) {
  return Object.entries(domainScoresDict).map(([_key, s]) => ({
    domainId: s.id ?? s.domain_id ?? _key,
    domainName: (s.domain ?? _key).replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
    score: s.domain_score ?? 0,
    percentage: (s.maturity_level ?? 1) * 20,
    maturityLevel: s.maturity_level ?? 1,
    maturityLevelName: s.maturity_level_name ?? '',
  }))
}

type TabId = 'overview' | 'answers' | 'recommendations' | 'export'

export function ResultsPage() {
  const { id } = useParams<{ id: string }>()
  const assessmentId = id ?? ''

  // Authorization
  const { actions: userActions } = useAuthorization()
  const { user } = useAuthentication()
  const isAdmin = user?.is_administrator === true
  const canView = (action: string | null) => {
    if (!action) return true // null = always visible
    return isAdmin || userActions.has(action)
  }

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: BarChart3, requiredAction: null as string | null },
    { id: 'answers' as const, label: 'Answers', icon: List, requiredAction: 'view_answers' as string | null },
    { id: 'recommendations' as const, label: 'Recommendations', icon: TrendingUp, requiredAction: 'view_recommendations' as string | null },
    { id: 'export' as const, label: 'Export', icon: Download, requiredAction: 'export_pdf' as string | null },
  ]

  const availableTabs = tabs.filter(tab => canView(tab.requiredAction))
  const validTabIds = availableTabs.map(t => t.id)

  // URL-driven tab state
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab')
  const activeTab: TabId = validTabIds.includes(rawTab as any) ? (rawTab as TabId) : (validTabIds[0] ?? 'overview')
  const setActiveTab = (tab: TabId) => {
    setSearchParams({ tab }, { replace: true })
  }

  // Report export state
  const [includeRecommendations, setIncludeRecommendations] = useState(true)
  const [includeAnswers, setIncludeAnswers] = useState(true)
  const [includeJustificationsYes, setIncludeJustificationsYes] = useState(true)
  const [includeAnswerRecommendations, setIncludeAnswerRecommendations] = useState(true)
  const [qnaCols, setQnaCols] = useState({
    type: true,
    level: true,
    weight: true,
    score: true
  })

  // Per-section recommendation toggles
  const [recSections, setRecSections] = useState({
    description: true,
    duration: true,
    team_size: true,
    impact: true,
    effort: true,
    business_value: true,
    budget_range: true,
    roi_estimate: true,
    technologies: true,
    success_metrics: true,
    recommended_tools: true,
    phases: true,
  })
  const toggleRecSection = (key: keyof typeof recSections) =>
    setRecSections(prev => ({ ...prev, [key]: !prev[key] }))

  // ── Data fetching (single backend call for all results) ──────────────────
  const { data: resultsRaw, isLoading: resultsLoading, error: resultsError } = useResults(assessmentId)
  const { data: progressData } = useAssessmentProgress(assessmentId)
  const recalculateMutation = useRecalculateScores(assessmentId)

  // DB maturity levels for color lookups (must be before early returns)
  const { data: mlRaw } = useEngineMaturityLevels()
  const mlLevels: MaturityLevel[] = (mlRaw as any)?.data ?? mlRaw ?? []

  // ── Open report page in new tab ────────────────────────────────────────────
  const openReport = useCallback(() => {
    const params = new URLSearchParams()
    params.set('recommendations', String(includeRecommendations))
    params.set('answers', String(includeAnswers))
    params.set('justifications_yes', String(includeJustificationsYes))
    params.set('answer_recommendations', String(includeAnswerRecommendations))
    params.set('col_type', String(qnaCols.type))
    params.set('col_level', String(qnaCols.level))
    params.set('col_weight', String(qnaCols.weight))
    params.set('col_score', String(qnaCols.score))
    // Pass section toggles
    if (includeRecommendations) {
      for (const [key, val] of Object.entries(recSections)) {
        if (!val) params.set(`sec_${key}`, 'false')
      }
    }
    window.open(`/assessments/${assessmentId}/report?${params.toString()}`, '_blank')
  }, [assessmentId, includeRecommendations, includeAnswers, includeJustificationsYes, includeAnswerRecommendations, qnaCols, recSections])

  // ── Loading / Error states ───────────────────────────────────────────────
  if (resultsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (resultsError) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-red-600">Error Loading Results</h2>
        <p className="text-gray-500 mt-2">Failed to load assessment results. Please try again.</p>
      </div>
    )
  }

  if (!resultsRaw) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">No Results Available</h2>
        <p className="text-gray-500 mt-2">Complete the assessment to view results.</p>
      </div>
    )
  }

  // ── Map backend response to component shapes ─────────────────────────────
  const overall = resultsRaw.overall_score ?? {}
  const overallScore: number = overall.domain_score ?? 0
  const maturityLevel: number = overall.maturity_level ?? Math.round(overallScore)

  const domainScores = resultsRaw.domain_scores
    ? mapDomainScores(resultsRaw.domain_scores)
    : []

  // ── Scope and comparison handling ──────────────────────────────────────────
  const assessmentScope = resultsRaw.assessment_scope ?? 'complete'
  const scopeLevel = resultsRaw.scope_level ?? null
  const comparisonTargets: string[] = resultsRaw.comparison_targets || []
  const previousScores = resultsRaw.previous_scores ?? null
  const industryBenchmarks = resultsRaw.industry_benchmarks ?? null
  const desiredLevel = resultsRaw.desired_maturity_level ?? scopeLevel ?? 3
  const industryName = resultsRaw.industry_name ?? undefined

  const benchmarksConfig: { key: string; name: string; color: string }[] = []
  if (assessmentScope === 'industry' || comparisonTargets.includes('industry_benchmarks')) {
    benchmarksConfig.push({ key: 'benchmark_industry', name: resultsRaw.industry_name ? `${resultsRaw.industry_name} Target` : 'Industry Standard', color: '#f59e0b' })
  }
  if (assessmentScope === 'level' || comparisonTargets.includes('desired_level')) {
    benchmarksConfig.push({ key: 'benchmark_desired', name: 'Target Level', color: '#8b5cf6' })
  }
  // Self-comparison (previous assessment) is always available if data exists
  if (previousScores) {
    benchmarksConfig.push({ key: 'benchmark_self', name: 'Previous Assessment', color: '#10b981' })
  }

  // Radar chart & Gap Analysis data
  const radarData = domainScores.map(d => {
    const key = d.domainName.toLowerCase().replace(/\s+/g, '_')
    const result: any = {
      domain: d.domainName,
      score: d.maturityLevel ?? 1,
      fullMark: 5,
    }

    if (comparisonTargets.includes('industry_benchmarks') && industryBenchmarks) {
      const bm = industryBenchmarks[key] ?? industryBenchmarks[d.domainName]
      if (bm) result.benchmark_industry = bm.target_level
    }
    
    if (comparisonTargets.includes('desired_level')) {
      result.benchmark_desired = desiredLevel
    }

    if (comparisonTargets.includes('self') && previousScores) {
      const ps = previousScores[key] ?? previousScores[d.domainName]
      if (ps) result.benchmark_self = ps.maturity_level
    }

    return result
  })

  const hasBenchmarks = benchmarksConfig.length > 0

  // Recommendations from backend
  const recommendations = (resultsRaw.recommendations ?? []).map((rec: any, i: number) => ({
    id: rec.id ?? `rec-${i}`,
    ...rec,
    priority: rec.priority ?? 'medium',
    category: rec.category ?? rec.domain ?? 'general',
  }))

  // Assessment meta
  const assessmentTitle = resultsRaw.assessment_title ?? `Assessment ${assessmentId}`
  const orgName = resultsRaw.organization_name ?? 'Organization'

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <h1 className="text-2xl font-bold text-gray-900">Assessment Results</h1>
        <p className="text-gray-600">
          {assessmentTitle}
          {orgName && (
            <span className="ml-1">· {orgName}</span>
          )}
          {industryName && (
            <span className="ml-1 text-muted-foreground">· {industryName}</span>
          )}
          {progressData && (
            <span className="ml-2 text-muted-foreground">
              · {progressData.total_answered}/{progressData.total_questions} questions answered
            </span>
          )}
        </p>
      </motion.div>

      {/* Recalculate Button */}
      <motion.div
        className="flex justify-end -mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.15 }}
      >
        {canView('recalculate_scores') && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={recalculateMutation.isPending}
            onClick={() => recalculateMutation.mutate()}
          >
            <RefreshCw className={`h-4 w-4 ${recalculateMutation.isPending ? 'animate-spin' : ''}`} />
            {recalculateMutation.isPending ? 'Recalculating...' : 'Recalculate Scores'}
          </Button>
        )}
      </motion.div>

      {/* Tab Navigation — underline style matching Recommendation Engine */}
      <motion.div
        className="border-b border-gray-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <nav className="-mb-px flex space-x-8">
          {availableTabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="h-4 w-4 inline mr-2" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </motion.div>

      {/* Tab Content */}
      <AnimatedTabContent activeTab={activeTab}>
        {/* ── Overview ──────────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <motion.div
            className="space-y-6"
            initial="hidden"
            animate="visible"
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12 } } }}
          >
            <motion.div
              variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] } } }}
            >
              <ScoreSummary
                overallScore={overallScore}
                maturityLevel={maturityLevel}
                desiredMaturityLevel={resultsRaw.desired_maturity_level}
                assessmentScope={assessmentScope}
                scopeLevel={scopeLevel}
                domainScores={domainScores}
                industryBenchmarks={industryBenchmarks}
              />
            </motion.div>

            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] } } }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                <RadarChartComponent
                  data={radarData}
                  title="IT Maturity Radar"
                  description="Performance across all IT maturity domains"
                  benchmarks={benchmarksConfig}
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35, duration: 0.4 }}
              >
                <GapAnalysisChart
                  data={radarData}
                  title="Gap Analysis"
                  description="Distance from targets"
                  benchmarks={benchmarksConfig}
                />
              </motion.div>
            </motion.div>

            {/* Comparison Benchmarks Card */}
            {hasBenchmarks && (
              <motion.div
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] } } }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Comparison Targets
                    </CardTitle>
                    <CardDescription>
                      Your actual maturity scores compared to selected target benchmarks
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {domainScores.map((d, index) => {
                        const key = d.domainName.toLowerCase().replace(/\s+/g, '_')
                        const actualLevel = d.maturityLevel ?? null
                        if (actualLevel === null) return null

                        return (
                          <motion.div
                            key={d.domainName}
                            className="flex flex-col md:flex-row items-start md:items-center justify-between p-3 rounded-lg border gap-4"
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 + index * 0.06, duration: 0.3 }}
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{d.domainName}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                Actual: <span className="font-semibold" style={{ color: getMaturityColorForLevel(mlLevels, actualLevel) }}>Level {actualLevel}</span>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-4 text-right">
                              {benchmarksConfig.map(bm => {
                                let bmTarget = null
                                if (bm.key === 'benchmark_industry' && industryBenchmarks) {
                                  bmTarget = industryBenchmarks[key]?.target_level ?? industryBenchmarks[d.domainName]?.target_level
                                } else if (bm.key === 'benchmark_desired') {
                                  bmTarget = desiredLevel
                                } else if (bm.key === 'benchmark_self' && previousScores) {
                                  bmTarget = previousScores[key]?.maturity_level ?? previousScores[d.domainName]?.maturity_level
                                }

                                if (bmTarget === null || bmTarget === undefined) return null

                                return (
                                  <div key={bm.key} className="flex flex-col items-end">
                                    <div className="text-[10px] text-gray-500 max-w-[100px] truncate" title={bm.name}>{bm.name}</div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold" style={{ color: bm.color }}>L{bmTarget}</span>
                                      {actualLevel >= bmTarget ? (
                                        <Badge className="bg-green-100 text-green-800 text-[10px] px-1 py-0 border-0 h-4">Met</Badge>
                                      ) : actualLevel >= bmTarget - 1 ? (
                                        <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1 py-0 border-0 h-4">Close</Badge>
                                      ) : (
                                        <Badge className="bg-red-100 text-red-800 text-[10px] px-1 py-0 border-0 h-4">Below</Badge>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </motion.div>
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* ── Recommendations ───────────────────────────────────────────── */}
        {activeTab === 'recommendations' && (
          <Recommendations
            recommendations={recommendations}
          />
        )}

        {/* ── Answers ────────────────────────────────────────────────────── */}
        {activeTab === 'answers' && (
          <AnswersTab assessmentId={assessmentId} />
        )}

        {/* ── Export ─────────────────────────────────────────────────────── */}
        {activeTab === 'export' && (
          <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Export Report</span>
                  </CardTitle>
                  <CardDescription>
                    Open a print-ready report page. Use your browser's Print function (Ctrl+P) to save as PDF.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Report Options */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Report Sections</h4>
                    <div className="space-y-2">
                      {/* Include Recommendations — parent toggle */}
                      <div className="rounded-lg border overflow-hidden">
                        <label className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors">
                          <div>
                            <div className="text-sm font-medium text-gray-700">Include Recommendations</div>
                            <div className="text-xs text-gray-400">Project initiative recommendations based on maturity gaps</div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={includeRecommendations}
                            onClick={() => setIncludeRecommendations(v => !v)}
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${includeRecommendations ? 'bg-blue-600' : 'bg-gray-200'}`}
                          >
                            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${includeRecommendations ? 'translate-x-4' : 'translate-x-0'}`} />
                          </button>
                        </label>

                        {/* Nested sub-section toggles — collapsible */}
                        {includeRecommendations && (
                          <div className="border-t border-gray-100 bg-gray-50/60">
                            <div className="ml-4 border-l-2 border-blue-200 pl-3 py-1.5 space-y-0.5">
                              {([
                                { key: 'description' as const, label: 'Description', desc: 'Initiative description text' },
                                { key: 'duration' as const, label: 'Duration', desc: 'Estimated project duration in weeks' },
                                { key: 'team_size' as const, label: 'Team Size', desc: 'Required team member count' },
                                { key: 'impact' as const, label: 'Impact Level', desc: 'Expected impact level' },
                                { key: 'effort' as const, label: 'Effort Level', desc: 'Required effort level' },
                                { key: 'business_value' as const, label: 'Business Value', desc: 'Expected business value narrative' },
                                { key: 'budget_range' as const, label: 'Budget Range', desc: 'Estimated budget range' },
                                { key: 'roi_estimate' as const, label: 'ROI Estimate', desc: 'Return on investment projection' },
                                { key: 'technologies' as const, label: 'Technologies', desc: 'Required technologies list' },
                                { key: 'success_metrics' as const, label: 'Success Metrics', desc: 'Success measurement criteria' },
                                { key: 'recommended_tools' as const, label: 'Recommended Tools', desc: 'Suggested tools & platforms' },
                                { key: 'phases' as const, label: 'Implementation Phases', desc: 'Phase-by-phase implementation plan' },
                              ]).map(({ key, label, desc }) => (
                                <label
                                  key={key}
                                  className="flex items-center justify-between py-1.5 px-2 rounded-md cursor-pointer hover:bg-gray-100/80 transition-colors"
                                >
                                  <div className="min-w-0">
                                    <div className="text-xs font-medium text-gray-600">{label}</div>
                                    <div className="text-[11px] text-gray-400 leading-tight">{desc}</div>
                                  </div>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={recSections[key]}
                                    onClick={() => toggleRecSection(key)}
                                    className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${recSections[key] ? 'bg-blue-500' : 'bg-gray-200'}`}
                                  >
                                    <span className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${recSections[key] ? 'translate-x-3' : 'translate-x-0'}`} />
                                  </button>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Include Answers */}
                      <div className="rounded-lg border overflow-hidden">
                        <label className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors">
                          <div>
                            <div className="text-sm font-medium text-gray-700">Include Answers</div>
                            <div className="text-xs text-gray-400">Full question-by-question breakdown per domain</div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={includeAnswers}
                            onClick={() => setIncludeAnswers(v => !v)}
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${includeAnswers ? 'bg-blue-600' : 'bg-gray-200'}`}
                          >
                            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${includeAnswers ? 'translate-x-4' : 'translate-x-0'}`} />
                          </button>
                        </label>

                        {/* Nested sub-section toggles — collapsible */}
                        {includeAnswers && (
                          <div className="border-t border-gray-100 bg-gray-50/60">
                            <div className="ml-4 border-l-2 border-blue-200 pl-3 py-1.5 space-y-0.5">
                              <label className="flex items-center justify-between py-1.5 px-2 rounded-md cursor-pointer hover:bg-gray-100/80 transition-colors">
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-gray-600">Justifications</div>
                                  <div className="text-[11px] text-gray-400 leading-tight">Include answer-matching justification (Yes / No - Gap)</div>
                                </div>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={includeJustificationsYes}
                                  onClick={() => setIncludeJustificationsYes((v: boolean) => !v)}
                                  className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${includeJustificationsYes ? 'bg-blue-500' : 'bg-gray-200'}`}
                                >
                                  <span className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${includeJustificationsYes ? 'translate-x-3' : 'translate-x-0'}`} />
                                </button>
                              </label>

                              <label className="flex items-center justify-between py-1.5 px-2 rounded-md cursor-pointer hover:bg-gray-100/80 transition-colors">
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-gray-600">Recommendations</div>
                                  <div className="text-[11px] text-gray-400 leading-tight">Include answer-specific recommendations</div>
                                </div>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={includeAnswerRecommendations}
                                  onClick={() => setIncludeAnswerRecommendations(v => !v)}
                                  className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${includeAnswerRecommendations ? 'bg-blue-500' : 'bg-gray-200'}`}
                                >
                                  <span className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${includeAnswerRecommendations ? 'translate-x-3' : 'translate-x-0'}`} />
                                </button>
                              </label>

                              <div className="my-2 border-t border-gray-200/60" />
                              <div className="px-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Columns to Display</div>

                              <label className="flex items-center justify-between py-1 px-2 rounded-md cursor-pointer hover:bg-gray-100/80 transition-colors">
                                <div className="text-xs font-medium text-gray-600">Question Type</div>
                                <button type="button" role="switch" aria-checked={qnaCols.type} onClick={() => setQnaCols(p => ({ ...p, type: !p.type }))} className={`relative inline-flex h-3 w-5 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${qnaCols.type ? 'bg-blue-400' : 'bg-gray-200'}`}>
                                  <span className={`pointer-events-none inline-block h-2 w-2 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${qnaCols.type ? 'translate-x-2' : 'translate-x-0'}`} />
                                </button>
                              </label>
                              <label className="flex items-center justify-between py-1 px-2 rounded-md cursor-pointer hover:bg-gray-100/80 transition-colors">
                                <div className="text-xs font-medium text-gray-600">Level</div>
                                <button type="button" role="switch" aria-checked={qnaCols.level} onClick={() => setQnaCols(p => ({ ...p, level: !p.level }))} className={`relative inline-flex h-3 w-5 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${qnaCols.level ? 'bg-blue-400' : 'bg-gray-200'}`}>
                                  <span className={`pointer-events-none inline-block h-2 w-2 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${qnaCols.level ? 'translate-x-2' : 'translate-x-0'}`} />
                                </button>
                              </label>
                              <label className="flex items-center justify-between py-1 px-2 rounded-md cursor-pointer hover:bg-gray-100/80 transition-colors">
                                <div className="text-xs font-medium text-gray-600">Weight</div>
                                <button type="button" role="switch" aria-checked={qnaCols.weight} onClick={() => setQnaCols(p => ({ ...p, weight: !p.weight }))} className={`relative inline-flex h-3 w-5 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${qnaCols.weight ? 'bg-blue-400' : 'bg-gray-200'}`}>
                                  <span className={`pointer-events-none inline-block h-2 w-2 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${qnaCols.weight ? 'translate-x-2' : 'translate-x-0'}`} />
                                </button>
                              </label>
                              <label className="flex items-center justify-between py-1 px-2 rounded-md cursor-pointer hover:bg-gray-100/80 transition-colors">
                                <div className="text-xs font-medium text-gray-600">Score</div>
                                <button type="button" role="switch" aria-checked={qnaCols.score} onClick={() => setQnaCols(p => ({ ...p, score: !p.score }))} className={`relative inline-flex h-3 w-5 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${qnaCols.score ? 'bg-blue-400' : 'bg-gray-200'}`}>
                                  <span className={`pointer-events-none inline-block h-2 w-2 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${qnaCols.score ? 'translate-x-2' : 'translate-x-0'}`} />
                                </button>
                              </label>

                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Open Report */}
                  <div className="flex items-center gap-4 pt-2">
                    <Button
                      onClick={openReport}
                      size="lg"
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open Report
                    </Button>
                    <span className="text-xs text-gray-400">Opens in a new tab — use Ctrl+P to print or save as PDF</span>
                  </div>
                </CardContent>
              </Card>
          </div>
        )}
      </AnimatedTabContent>
    </motion.div>
  )
}

